import { publicUrl } from "@/utils/public-url";
/**
 * 이용권 스토어 설정 (2안 v2: 같은 웹, 라우트만 분리)
 *
 * 스토어는 의료진용 웹과 같은 SPA 안에서 라우트(/store)로만 분리된다.
 * 기존 로그인 세션(쿠키)·암호화 클라이언트를 그대로 사용하므로 별도 로그인/SSO가 없다.
 *
 * ❗ 도메인 미분리에 따른 유의점(5안과의 차이): PG SDK·결제 코드가 의료기기 SW 번들에
 * 포함된다 — 변경허가/IEC 62304/SOUP/릴리스 절차 영향은 QMRA 검토 필요.
 * (src/store/PG-REVIEW-CHECKLIST.md §0 참고)
 */

export enum PgProvider {
  TOSS = "TOSS",
  KG_INICIS = "KG_INICIS",
}

/** 스토어 라우트 경로 */
export const STORE_PATHS = {
  /** 스토어 메인 (상품 목록, 의료진 로그인 필요) */
  ROOT: "/store",
  /** 주문서 */
  PURCHASE: "/store/purchase",
  /** 결제 내역 (계정 소속 병원 범위) */
  PAYMENTS: "/store/payments",
  /** 브랜드페이 리다이렉트 URL 페이지 (토스 개발자센터 등록값, 주문서와 같은 도메인) */
  BRANDPAY_CALLBACK: "/store/brandpay/callback",
  /** 비로그인 공개 취소·환불 정책 */
  REFUND_POLICY: "/policy/refund",
  /** 비로그인 공개 판매 정책 */
  SALES_POLICY: "/policy/sales",
  /** 에스알파 관리자: 활성화 기록·수수료 정산 */
  ADMIN_SETTLEMENTS: "/admin/home/settlements",
} as const;

/**
 * 사용할 PG사 선택 (기본: 토스페이먼츠)
 * .env 에서 VITE_STORE_PG=inicis 로 변경 가능
 */
export const getPgProvider = (): PgProvider => {
  const raw = (import.meta.env.VITE_STORE_PG ?? "toss").toLowerCase();
  return raw === "inicis" ? PgProvider.KG_INICIS : PgProvider.TOSS;
};

/**
 * 토스 결제 흐름 — store-api 의 STORE_TOSS_FLOW 와 **반드시 같아야** 한다.
 *  - brandpay (기본): 병원 법인카드를 등록해 두고 반복 결제. API 개별 연동 키(ck) 사용
 *  - widget: 결제위젯. 결제위젯 연동 키(gck) 사용
 * .env 에서 VITE_STORE_TOSS_FLOW=widget 으로 바꿀 수 있다 (브랜드페이 키 발급 전 로컬 테스트용).
 */
export type TossFlow = "brandpay" | "widget";
export const getTossFlow = (): TossFlow =>
  (import.meta.env.VITE_STORE_TOSS_FLOW ?? "brandpay").toLowerCase() === "widget" ? "widget" : "brandpay";

/**
 * 브랜드페이 리다이렉트 URL — 토스 SDK 가 브라우저에서 이 URL 을 GET 하여 **JSON 응답(Access Token)** 을 읽는다.
 * 그래서 화면(HTML)이 아닌 store-api 의 비인증 콜백 API 여야 하고, SDK 가 customerKey 를 비워 보내는 경우가 있어
 * 우리 키를 `ownerKey` 로 미리 붙인다. 토스 개발자센터에는 쿼리 없는 기본 주소
 * `https://<API 도메인>/store/brandpay/callback` 를 등록한다.
 */
export const getBrandpayRedirectUrl = (customerKey: string): string =>
  `${import.meta.env.VITE_API_BASE_URL}/store/brandpay/callback?ownerKey=${encodeURIComponent(customerKey)}`;

/**
 * 백엔드 결제 API가 준비되기 전까지 목(mock) 데이터로 동작시키는 플래그
 * .env 에서 VITE_STORE_USE_MOCK=true
 */
export const isStoreMockMode = (): boolean => import.meta.env.VITE_STORE_USE_MOCK === "true";

/**
 * 결제창(위젯) 모드 — 기본값. 토스페이먼츠 결제창을 실제로 띄운다 (테스트 키면 실 청구 없음, 라이브 키면 실 결제).
 * 백엔드 없이 화면만 볼 때 .env 에서 VITE_STORE_PAYMENT=mock 으로 끄면 결제 경로가 목으로 처리된다.
 */
export const isPaymentWidgetTestMode = (): boolean =>
  (import.meta.env.VITE_STORE_PAYMENT ?? "widget") !== "mock";

/** 결제 경로를 목으로 처리할지 — 위젯 테스트 모드가 켜져 있으면 항상 실제 결제창을 사용한다 */
export const isPaymentMockMode = (): boolean =>
  !isPaymentWidgetTestMode() && isStoreMockMode();

/**
 * 토스페이먼츠 클라이언트 키 (공개 키, 시크릿 키는 절대 프론트에 두지 않는다)
 * 기본값은 토스페이먼츠 공식 문서의 공개 테스트 키.
 */
export const getTossClientKey = (): string =>
  import.meta.env.VITE_TOSS_CLIENT_KEY ?? "test_gck_docs_Ovk5rk1EwkEbP0W43n07xlzm";

//MARK:- 팝업 구매 모드 (치료 기간 설정 화면의 모달에서 이용권 선택 → 새 창 주문서에서 결제 → 결제 완료 시 치료 등록까지 → 원래 창을 치료 상세로)
/** window.open 의 창 이름 — 같은 이름으로 다시 열면 기존 팝업을 재사용한다 */
export const STORE_POPUP_WINDOW_NAME = "sat-store-purchase";
/** 팝업 창 안에서만 세팅되는 플래그 (sessionStorage 는 창 단위라 원래 탭에는 영향 없음) */
const STORE_POPUP_FLAG_KEY = "store:popup";
/** 팝업 → 원래 창: 치료 등록까지 완료됨 */
export const STORE_TREATMENT_ADDED_MESSAGE_TYPE = "store:treatment-added";

export interface StoreTreatmentAddedMessage {
  type: typeof STORE_TREATMENT_ADDED_MESSAGE_TYPE;
  patientUuid: string;
  treatmentUuid: string;
}

/** 스토어 진입 URL 에 popup=1 이 있으면 이 창을 팝업 모드로 표시한다 (이후 내부 이동에도 유지) */
export const enterStorePopupModeIfRequested = (search: string): void => {
  try {
    if (new URLSearchParams(search).get("popup") === "1") {
      sessionStorage.setItem(STORE_POPUP_FLAG_KEY, "1");
    }
  } catch {
    // sessionStorage 접근 불가 환경 — 일반 모드로 동작
  }
};

export const isStorePopupMode = (): boolean => {
  try {
    return sessionStorage.getItem(STORE_POPUP_FLAG_KEY) === "1";
  } catch {
    return false;
  }
};

/**
 * 치료 기간 설정 화면이 넘겨주는 등록 정보. 결제가 끝나면 스토어(팝업)가 이 값으로 치료를 등록한다.
 * localStorage 에 두는 이유: 팝업 창과 원래 창이 공유해야 하고, 토스 리다이렉트를 거쳐도 남아야 한다.
 */
export interface TreatmentDraft {
  patientUuid: string;
  productId: string;
  months: number;
  /** 현재 치료 차수 (등록될 차수 = step + 1) */
  step: number;
  visitDate: string;
  memo: string;
  createdAt: string;
}

const TREATMENT_DRAFT_KEY_PREFIX = "store:treatment-draft:";

export const saveTreatmentDraft = (draft: TreatmentDraft): void => {
  localStorage.setItem(`${TREATMENT_DRAFT_KEY_PREFIX}${draft.patientUuid}`, JSON.stringify(draft));
};

export const loadTreatmentDraft = (patientUuid: string): TreatmentDraft | null => {
  try {
    const raw = localStorage.getItem(`${TREATMENT_DRAFT_KEY_PREFIX}${patientUuid}`);
    return raw ? (JSON.parse(raw) as TreatmentDraft) : null;
  } catch {
    return null;
  }
};

export const clearTreatmentDraft = (patientUuid: string): void => {
  localStorage.removeItem(`${TREATMENT_DRAFT_KEY_PREFIX}${patientUuid}`);
};

/** 주문서 URL — register=1 이면 결제 완료 후 드래프트로 치료 등록까지 진행한다 */
export const buildStorePurchaseUrl = (productId: string, patientUuid: string, popup: boolean): string => {
  const params = new URLSearchParams({ productId, patient: patientUuid, register: "1" });
  if (popup) {
    params.set("popup", "1");
  }
  return `${STORE_PATHS.PURCHASE}?${params.toString()}`;
};

/** 팝업(스토어)이 세션 만료를 감지했을 때 원래 창에 알리는 메시지 — 원래 창이 로그인으로 이동한다 */
export const STORE_SESSION_EXPIRED_MESSAGE_TYPE = "store:session-expired";
export interface StoreSessionExpiredMessage {
  type: typeof STORE_SESSION_EXPIRED_MESSAGE_TYPE;
}

/** 팝업에서 원래 창으로 세션 만료를 알린다 (같은 origin 에만). 알렸으면 true */
export const notifyStoreSessionExpired = (): boolean => {
  const opener = window.opener as Window | null;
  if (!opener || opener.closed) {
    return false;
  }
  const message: StoreSessionExpiredMessage = { type: STORE_SESSION_EXPIRED_MESSAGE_TYPE };
  opener.postMessage(message, window.location.origin);
  return true;
};

/**
 * 주문서 팝업을 **빈 창으로 먼저** 연다 — 클릭 핸들러 안에서 동기적으로 호출해야 팝업이 차단되지 않는다.
 * 호출부가 세션 확인(비동기)을 마친 뒤 `navigateStorePurchasePopup` 으로 주문서를 로드하거나, 실패 시 닫는다.
 */
export const openBlankStorePopup = (): Window | null =>
  window.open("about:blank", STORE_POPUP_WINDOW_NAME, "width=620,height=900,resizable=yes,scrollbars=yes");

/** 빈 팝업에 주문서 URL 을 로드한다 (배포 base 포함) */
export const navigateStorePurchasePopup = (popup: Window, productId: string, patientUuid: string): void => {
  popup.location.href = publicUrl(buildStorePurchaseUrl(productId, patientUuid, true));
};

/**
 * 선택한 이용권의 주문서를 새 창으로 연다. 팝업이 차단되면 null — 호출부는 같은 탭 이동으로 폴백해야 한다.
 * 반드시 클릭 핸들러에서 동기적으로 호출할 것 (await 뒤에 열면 브라우저가 차단한다).
 * 크기는 토스 결제창 오버레이가 잘리지 않을 만큼 넉넉하게 둔다.
 */
export const openStorePurchasePopup = (productId: string, patientUuid: string): Window | null =>
  window.open(
    // window.open 은 라우터를 거치지 않으므로 배포 base(/001/)를 직접 붙인다
    publicUrl(buildStorePurchaseUrl(productId, patientUuid, true)),
    STORE_POPUP_WINDOW_NAME,
    "width=620,height=900,resizable=yes,scrollbars=yes"
  );

/** 팝업 창에서 원래 창으로 치료 등록 완료를 알린다 (같은 origin 에만) */
export const notifyTreatmentAdded = (patientUuid: string, treatmentUuid: string): void => {
  const opener = window.opener as Window | null;
  if (!opener || opener.closed) {
    return;
  }
  const message: StoreTreatmentAddedMessage = {
    type: STORE_TREATMENT_ADDED_MESSAGE_TYPE,
    patientUuid,
    treatmentUuid,
  };
  opener.postMessage(message, window.location.origin);
};

/** 브랜드페이 리다이렉트 페이지가 읽을 마지막 customerKey (SDK 가 code 만 넘기는 경우 대비, 같은 origin 의 localStorage) */
const BRANDPAY_CUSTOMER_KEY_STORAGE = "store-brandpay:last-customer-key";
export const rememberBrandpayCustomerKey = (customerKey: string): void => {
  try {
    localStorage.setItem(BRANDPAY_CUSTOMER_KEY_STORAGE, customerKey);
  } catch {
    /* 저장 불가 환경이면 무시 — 쿼리 파라미터에 customerKey 가 오면 그걸 쓴다 */
  }
};
export const recallBrandpayCustomerKey = (): string | null => {
  try {
    return localStorage.getItem(BRANDPAY_CUSTOMER_KEY_STORAGE);
  } catch {
    return null;
  }
};
